import { addDays, addYears, differenceInMilliseconds, format } from 'date-fns';
import { create } from 'zustand';

import { Notice } from '../api/types';

const NOTICE_STORAGE_KEY_PREFIX = 'MINDD_Notice_';

const isHideNotice = (notice: Notice): boolean => {
  const today = new Date();
  const start = new Date(notice.startDate);

  if (differenceInMilliseconds(today, start) < 0) {
    return true;
  } else if (notice.endDate && differenceInMilliseconds(today, new Date(notice.endDate)) > 0) {
    return true;
  } else if (sessionStorage.getItem(`${NOTICE_STORAGE_KEY_PREFIX}${notice.timestampId}`)) {
    return true;
  }

  try {
    const date = localStorage.getItem(`${NOTICE_STORAGE_KEY_PREFIX}${notice.timestampId}`);
    return !!date && differenceInMilliseconds(today, new Date(date)) <= 0;
  } catch (e) {
    console.warn('[GlobalUIStore] localStorage.getItem 실패', e);
    return false;
  }
};

/**
 * 화면에서 사라진 공지의 잔여 localStorage 키를 정리한다.
 * 공지 목록이 로드될 때(App) 1회 호출한다.
 */
export const cleanupNoticeInStorage = (notice: Notice[]) => {
  if (notice.length > 0) {
    try {
      const timestampIds = notice.map((n) => n.timestampId);
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(NOTICE_STORAGE_KEY_PREFIX)) {
          const timestampId = key.replace(NOTICE_STORAGE_KEY_PREFIX, '');
          if (!timestampIds.includes(Number(timestampId))) {
            localStorage.removeItem(key);
          }
        }
      }
    } catch (e) {
      console.warn('[GlobalUIStore] localStorage 정리 실패', e);
    }
  }
};

/**
 * 서버 공지 목록(useFetchNotice가 소유)을 표시 대상으로 필터링하는 순수 파생 함수.
 * 로그인 여부·발행조건(paid_survey/stim/telecare)·숨김 상태(localStorage/sessionStorage)를 반영한다.
 * 공지 배열 자체는 스토어에 복제하지 않고 React Query 캐시를 단일 진실 공급원으로 둔다.
 */
export const filterVisibleNotices = (
  notices: Notice[],
  isLogin: boolean,
  paidSurvey?: boolean,
  stim?: boolean,
  telecare?: boolean,
): Notice[] =>
  notices
    .filter((v) => (isLogin ? true : !v.needLogin))
    .filter((v) => {
      if (isLogin) {
        if (v.publishCondition === 'paid_survey') {
          return paidSurvey;
        } else if (v.publishCondition === 'stim') {
          return stim;
        } else if (v.publishCondition === 'telecare') {
          return telecare;
        }
      }

      return true;
    })
    .filter((v) => !isHideNotice(v));

export interface GlobalUIState {
  expanded: boolean;
  setExpanded: (flag: boolean) => void;
  setHideNotice: (notice: Notice, optionChecked: boolean) => void;
}

export const useGlobalUIStore = create<GlobalUIState>((set) => ({
  expanded: false,

  setExpanded: (flag) => set({ expanded: flag }),

  setHideNotice: (notice, optionChecked) => {
    if (optionChecked) {
      try {
        const value = notice.enableCheckboxToDisableForever
          ? format(addYears(new Date(), 100), 'yyyy-MM-dd HH:mm:ss')
          : format(addDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
        localStorage.setItem(`${NOTICE_STORAGE_KEY_PREFIX}${notice.timestampId}`, value);
      } catch (e) {
        console.warn('[GlobalUIStore] localStorage.setItem 실패', e);
      }
    } else {
      sessionStorage.setItem(`${NOTICE_STORAGE_KEY_PREFIX}${notice.timestampId}`, 'true');
    }
  },
}));
